/**
 * Form autosave hook.
 *
 * Persists form state to localStorage under a per-form key so a customer can
 * recover their progress after a network error, accidental tab close, or
 * browser crash. Drafts expire after 24h.
 *
 * Usage:
 *   const { restoreDraft, clearDraft, hasDraft, draftAge } =
 *     useFormAutosave('contact', formData, setFormData, { enabled: !isSuccess });
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const STORAGE_PREFIX = 'portico:draft:';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 500;

const safeRead = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const safeWrite = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded or private mode — ignore */
  }
};

const safeRemove = (key) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
};

const isEmptyValue = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'boolean') return v === false;
  return false;
};

const hasMeaningfulData = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => !isEmptyValue(v));
};

export const useFormAutosave = (formId, formData, setFormData, options = {}) => {
  const { enabled = true } = options;
  const key = `${STORAGE_PREFIX}${formId}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [draftAge, setDraftAge] = useState(null);
  const timeoutRef = useRef(null);
  const hasMounted = useRef(false);

  // On mount: detect an existing draft.
  useEffect(() => {
    const existing = safeRead(key);
    if (existing && hasMeaningfulData(existing.data)) {
      setHasDraft(true);
      setDraftAge(Date.now() - existing.savedAt);
    }
    hasMounted.current = true;
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist (debounced) on every form-data change after mount.
  useEffect(() => {
    if (!hasMounted.current || !enabled) return;
    if (!hasMeaningfulData(formData)) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      safeWrite(key, { savedAt: Date.now(), data: formData });
    }, SAVE_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, enabled]);

  const restoreDraft = useCallback(() => {
    const existing = safeRead(key);
    if (existing && existing.data) {
      setFormData((prev) => ({ ...prev, ...existing.data }));
      setHasDraft(false);
      return true;
    }
    return false;
  }, [key, setFormData]);

  const clearDraft = useCallback(() => {
    safeRemove(key);
    setHasDraft(false);
    setDraftAge(null);
  }, [key]);

  const dismissDraftBanner = useCallback(() => {
    setHasDraft(false);
  }, []);

  return { hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner };
};

export default useFormAutosave;
